import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCommentIcon from '@mui/icons-material/AddComment';
import { fetchApi } from '../services/api';
import CounselingDetailDialog from './CounselingDetailDialog';

interface Customer {
  고객번호: number;
  업체번호: number;
  고객명: string;
  성별: string;
  영문성: string;
  영문이름: string;
  대표전화: string;
  이메일: string;
  문자수신동의: boolean;
  이메일수신동의: boolean;
  상태코드: string;
  가입경로: string;
  최근접속일시: string;
  등록일시: string;
  변경일시: string;
  비고: string;
}

interface CounselingListItem {
  견적명: string;
  여행시작일: string;
  여행종료일: string;
  인원: number;
  성인판매가: number;
  총예상수익: number;
  상담번호: number;  // 추가
}

interface CounselingListResponse {
  table: {
    견적명: string;
    여행시작일: string;
    여행종료일: string;
    인원: number;
    성인판매가: number;
    총예상수익: number;
    상담번호: number;
  }[];
}

interface CustomerDetailDialogProps {
  open: boolean;
  onClose: () => void;
  customer: Customer | null;
  onSave: () => void;
}

const CustomerDetailDialog: React.FC<CustomerDetailDialogProps> = ({
  open,
  onClose,
  customer,
  onSave,
}) => {
  // 초기 상태값 정의
  const initialFormState = {
    고객번호: 0,
    업체번호: Number(localStorage.getItem('CompanyNo')),
    고객명: '',
    성별: '',
    영문성: '',
    영문이름: '',
    대표전화: '',
    이메일: '',
    문자수신동의: false,
    이메일수신동의: false,
    상태코드: '',
    가입경로: '',
    최근접속일시: '',
    등록일시: '',
    변경일시: '',
    비고: '',
  };

  const [formData, setFormData] = useState<Customer>(initialFormState);
  const [counselingDialogOpen, setCounselingDialogOpen] = useState(false);
  const [counselingList, setCounselingList] = useState<CounselingListItem[]>([]);
  const [counselingNo, setCounselingNo] = useState<number>(0);

  // 다이얼로그가 닫힐 때 상태 초기화
  useEffect(() => {
    if (!open) {
      setFormData(initialFormState);
      setCounselingDialogOpen(false);
      setCounselingList([]);
      setCounselingNo(0);
    }
  }, [open]);

  // customer prop이 변경될 때 formData 업데이트
  useEffect(() => {
    if (customer && open) {
      setFormData(customer);
    }
  }, [customer, open]);

  // 고객별 상담 리스트 조회
  const fetchCounselingList = async (customerNo: number) => {
    try {
      const response = await fetchApi<CounselingListResponse>('ITN_S_상담리스트_고객별', [
        { "컬럼명": "@고객번호", "값": customerNo.toString() }
      ]);

      if (response.data?.table) {
        setCounselingList(response.data.table);
      }
    } catch (error) {
      console.error('Failed to fetch counseling list:', error);
    }
  };

  // customer prop이 변경될 때 상담 리스트 조회
  useEffect(() => {
    if (customer?.고객번호 && open) {
      fetchCounselingList(customer.고객번호);
    }
  }, [customer, open]);

  const handleChange = (field: keyof Customer) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleCheckboxChange = (field: keyof Customer) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
      [`${field}일시`]: event.target.checked ? new Date().toISOString() : '',
    });
  };

  const handleSubmit = async () => {
    try {
      const response = await fetchApi('ITN_T_간편일정고객저장', [
        { "컬럼명": "@업체번호", "값": formData.업체번호.toString() },
        { "컬럼명": "@고객번호", "값": formData.고객번호.toString() },
        { "컬럼명": "@이메일", "값": formData.이메일 },
        { "컬럼명": "@고객명", "값": formData.고객명 },
        { "컬럼명": "@성별", "값": formData.성별 },
        { "컬럼명": "@영문성", "값": formData.영문성 },
        { "컬럼명": "@영문이름", "값": formData.영문이름 },
        { "컬럼명": "@대표전화", "값": formData.대표전화 },
        { "컬럼명": "@문자수신동의", "값": formData.문자수신동의 ? "1" : "0" },
        { "컬럼명": "@이메일수신동의", "값": formData.이메일수신동의 ? "1" : "0" },
        { "컬럼명": "@상태코드", "값": formData.상태코드 },
        { "컬럼명": "@가입경로", "값": formData.가입경로 },
        { "컬럼명": "@비고", "값": formData.비고 },
      ]);

      if (response.data) {
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Failed to save customer:', error);
    }
  };

  const handleCounselingOpen = () => {
    setCounselingDialogOpen(true);
  };

  // 고객 삭제 함수
  const handleCustomerDelete = async () => {
    if (!customer?.고객번호) return;

    try {
      const response = await fetchApi('ITN_T_간편일정고객삭제', [
        { "컬럼명": "@고객번호", "값": customer.고객번호.toString() }
      ]);

      if (response.data) {
        onSave(); // 목록 새로고침
        onClose();
      }
    } catch (error) {
      console.error('Failed to delete customer:', error);
    }
  };

  // TextField와 Select 컴포넌트의 공통 스타일
  const inputStyle = {
    height: '42px',
    '& .MuiOutlinedInput-root': {
      height: '42px'
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={false}
      PaperProps={{
        sx: { 
          width: '1000px',
          maxHeight: '90vh'  // 고정 높이 제거, 최대 높이만 설정
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {customer ? '고객 정보 수정' : '신규 고객 등록'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap:  1}}>
          {customer && (
            <Button
              onClick={handleCounselingOpen}
              variant="contained"
              startIcon={<AddCommentIcon />}
              sx={{ 
                height: '36px',
                bgcolor: 'white', 
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'grey.100',
                }
              }}>
              상담등록
            </Button>
          )}
          <Button 
            onClick={handleSubmit}
            variant="contained"
            sx={{ 
              height: '36px',
              bgcolor: 'white', 
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100',
              }
            }}>
            저장
          </Button>
          {customer && (
            <Button
              onClick={handleCustomerDelete}
              variant="contained"
              color="error"
              sx={{ height: '36px' }}
            >
              고객삭제
            </Button>
          )}
          <Button 
            onClick={onClose}
            variant="contained"
            sx={{ 
              height: '36px',
              bgcolor: 'white', 
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100',
              }
            }}>
            닫기
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 4 }}>
        <Grid container spacing={2} sx={{ pt: 4 }}>
          {/* 고객 정보 입력 폼 */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 1,
                      color: 'text.secondary',
                      fontSize: '13px'
                    }}
                  >
                    고객명
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="고객명을 입력하세요"
                    value={formData.고객명}
                    onChange={handleChange('고객명')}
                    sx={inputStyle}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 1,
                      color: 'text.secondary',
                      fontSize: '13px'
                    }}
                  >
                    성별
                  </Typography>
                  <FormControl fullWidth size="small" sx={inputStyle}>
                    <Select
                      value={formData.성별}
                      onChange={(e) => setFormData({ ...formData, 성별: e.target.value })}
                    >
                      <MenuItem value="남성  ">남성</MenuItem>
                      <MenuItem value="여성  ">여성</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 1,
                      color: 'text.secondary',
                      fontSize: '13px'
                    }}
                  >
                    영문 성
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="영문 성을 입력하세요"
                    value={formData.영문성}
                    onChange={handleChange('영문성')}
                    sx={inputStyle}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 1,
                      color: 'text.secondary',
                      fontSize: '13px'
                    }}
                  >
                    영문 이름
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="영문 이름을 입력하세요"
                    value={formData.영문이름}
                    onChange={handleChange('영문이름')}
                    sx={inputStyle}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 1,
                      color: 'text.secondary',
                      fontSize: '13px'
                    }}
                  >
                    이메일
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="이메일을 입력하세요"
                    value={formData.이메일}
                    onChange={handleChange('이메일')}
                    sx={inputStyle}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 1,
                      color: 'text.secondary',
                      fontSize: '13px'
                    }}
                  >
                    연락처
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="연락처를 입력하세요"
                    value={formData.대표전화}
                    onChange={handleChange('대표전화')}
                    sx={inputStyle}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 3,

                }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.문자수신동의}
                        onChange={handleCheckboxChange('문자수신동의')}
                        size="small"
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '14px' }}>
                        문자 수신 동의
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.이메일수신동의}
                        onChange={handleCheckboxChange('이메일수신동의')}
                        size="small"
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '14px' }}>
                        이메일 수신 동의
                      </Typography>
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* 비고 영역 */}
          <Grid item xs={12} sx={{ mb: 9 }}>
            <TextField
              fullWidth
              multiline
              rows={4}  // 비고 영역 높이 줄임
              label="비고"
              value={formData.비고}
              onChange={handleChange('비고')}
              sx={inputStyle}
            />
          </Grid>

          {/* 상담 리스트 */}
          {counselingList.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                mb: 1, 
                mt: 2,
                justifyContent: 'space-between'
              }}>
                <Typography variant="subtitle1">상담 내역</Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '14px',
                    color: 'text.secondary'
                  }}
                >
                  * 하단 상담리스트를 클릭하면 상담세부를 볼 수 있습니다.
                </Typography>
              </Box>
              <TableContainer component={Paper} sx={{ 
                height: 'auto',
                maxHeight: '400px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ height: '52px' }}>견적명</TableCell>
                      <TableCell sx={{ height: '52px' }}>여행기간</TableCell>
                      <TableCell align="right" sx={{ height: '52px' }}>인원</TableCell>
                      <TableCell align="right" sx={{ height: '52px' }}>판매가</TableCell>
                      <TableCell align="right" sx={{ height: '52px' }}>예상수익</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {counselingList.map((item, index) => (
                      <TableRow 
                        key={index}
                        hover
                        onClick={() => {
                          setCounselingNo(item.상담번호);
                          setCounselingDialogOpen(true);
                        }}
                        sx={{ 
                          cursor: 'pointer',
                          height: '52px',  // 행 높이 설정
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <TableCell sx={{ height: '52px' }}>{item.견적명}</TableCell>
                        <TableCell sx={{ height: '52px' }}>
                          {item.여행시작일.slice(0, 10)} ~ {item.여행종료일.slice(0, 10)}
                        </TableCell>
                        <TableCell align="right" sx={{ height: '52px' }}>{item.인원}</TableCell>
                        <TableCell align="right" sx={{ height: '52px' }}>
                          {item.성인판매가.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ height: '52px' }}>
                          {item.총예상수익.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      {/* 상담상세 다이얼로그 */}
      {counselingDialogOpen && counselingNo > 0 && (
        <CounselingDetailDialog
          counselingNo={counselingNo.toString()}
          open={counselingDialogOpen}
          onClose={() => {
            setCounselingDialogOpen(false);
            setCounselingNo(0);
          }}
          initialData={{
            요청자명: formData.고객명,
            요청자이메일: formData.이메일,
            요청자휴대폰: formData.대표전화,
            고객번호: formData.고객번호
          }}
        />
      )}
    </Dialog>
  );
};

export default CustomerDetailDialog; 